<script>
  import InputContainer from '@components/inputs/input-container.svelte'
  import SegmentedControl from '@components/inputs/segmented-control.svelte'
  import BooleanSelect from '@components/inputs/boolean-select.svelte'
  import BooleanControl from '@components/inputs/boolean-control.svelte'
  import PasswordInput from '@components/inputs/password-input.svelte'
  import EmailInput from '@components/inputs/email-input.svelte'
  import RadioGroup from '@components/inputs/radio-group.svelte'
  // import DateInput from '@components/inputs/date-input.svelte'
  import CheckboxGroup from '@components/inputs/checkbox-group.svelte'
  import CheckboxItem from '@components/inputs/checkbox-item.svelte'
  import Select from '@components/inputs/select.svelte'
  import Autocomplete from '@components/inputs/autocomplete-single.svelte'
  import AutocompleteMultiple from '@components/inputs/autocomplete-multiple.svelte'
  import ColorPicker from '@components/general/color-picker.svelte'
  import TextEditor from '@components/inputs/text-editor.svelte'
  import { cn } from "@lib/utils"
  import { watch } from 'runed'

  let {
    name,
    value = $bindable(),
    label,
    class: className,
    inputClass,
    type = "text",
    cols = 12,
    options = [],
    blank = false,
    multiple = false,
    hint = "",
    children,
    prefix,
    suffix,
    prefixClass,
    suffixClass,
    labelContent,
    labelClass,
    innerContent,
    labelKey = "label",
    valueKey = "value",
    validate: validateOn = "blur",
    onValidate,
    patternMessage,
    inputRef = $bindable(null),
    ...inputProps
  } = $props()

  let containerClass = $derived(cn(
    "form-item",
    {
      "flex-1": cols === 0,
      "col-span-12 lg:col-span-1": cols === 1,
      "col-span-12 lg:col-span-2": cols === 2,
      "col-span-12 lg:col-span-3": cols === 3,
      "col-span-12 lg:col-span-4": cols === 4,
      "col-span-12 lg:col-span-5": cols === 5,
      "col-span-12 lg:col-span-6": cols === 6,
      "col-span-12 lg:col-span-7": cols === 7,
      "col-span-12 lg:col-span-8": cols === 8,
      "col-span-12 lg:col-span-9": cols === 9,
      "col-span-12 lg:col-span-10": cols === 10,
      "col-span-12 lg:col-span-11": cols === 11,
      "col-span-12 lg:col-span-12": cols === 12,
    },
    className
  ))

  let labelBaseClass = $derived.by(() => {
    let baseClass = "text-xs text-gray-500 font-normal"
    if (type === "checkbox-group") return baseClass + " h-4 mb-4"
    return baseClass + " h-4"
  })

  let errorMessage = $state(false)
  let dirty = $state(false)

  let inputValidityClass = $derived(valid ? "" : "input-error")

  let valid = $derived(!errorMessage)

  function handleInput() {
    dirty = true
    inputProps.oninput?.()
    if (validateOn && (!valid || validateOn === "input")) validate()
  }

  function handleFileChange(e) {
    inputProps.onchange?.(e)
    const files = Array.from(e.currentTarget?.files || [])
    value = multiple ? files : (files[0] || null)
    if (validateOn && (!valid || validateOn === "input")) validate()
  }

  function handleBlur() {
    inputProps.onblur?.()
    if (validateOn && dirty && validateOn === "blur") validate()
  }

  function validate() {
    const validity = inputRef?.validity
    errorMessage = (validity.patternMismatch && patternMessage) ? patternMessage : inputRef.validationMessage
  }

  watch(() => valid, (newValid) => {
    onValidate?.(valid, { message: errorMessage })
  })

</script>

{#snippet contents()}
  {#if type === "textarea"}
    <textarea {name} bind:this={inputRef} bind:value={value} class={cn("input h-36 min-h-12", inputClass, inputValidityClass)} {...inputProps}></textarea>
  {:else if type === "boolean"}
    <BooleanControl {name} bind:value={value} {...inputProps} />
  {:else if type === "boolean-filter"}
    <BooleanSelect {name} bind:value={value} {...inputProps} />
  {:else if type === "email"}
    <EmailInput {name} bind:value={value} validate={validateOn} {...inputProps} />
  {:else if type === "password"}
    <PasswordInput {name} bind:value={value} validate={validateOn} {...inputProps} />
  {:else if type === "radio"}
    <RadioGroup {name} bind:group={value} {options} />
  {:else if type === "checkbox"}
    <CheckboxItem {label} {name} bind:checked={value} {...inputProps} />
  {:else if type === "checkbox-group"}
    <CheckboxGroup {name} bind:group={value} {options} />
  {:else if type === "autocomplete" && multiple}
    <AutocompleteMultiple {name} bind:value={value} items={options} {labelKey} {valueKey} {...inputProps} bind:this={inputRef} />
  {:else if type === "autocomplete"}
    <Autocomplete {name} bind:value={value} items={options} {labelKey} {valueKey} {...inputProps} bind:this={inputRef} />
  {:else if type === "custom-select"}
    <Select {name} bind:value={value} items={options} />
  {:else if type === "segmented-control"}
    <SegmentedControl {name} bind:value={value} {options} />
  {:else if type === "color"}
    <ColorPicker bind:color={value} buttonProps={{ class: "h-10 w-full" }} {...inputProps} />
  {:else if type === "richtext"}
    <TextEditor bind:value class={inputClass} />
  {:else if type === "select" || options.length > 0}
    <select {name} bind:this={inputRef} bind:value={value} class={cn("w-full", inputClass, inputValidityClass, className)} {...inputProps}>
      {#if blank}
        <option value="">{blank === true ? "" : blank}</option>
      {/if}
      {#each options as option}
        <option value={option[valueKey]}>{option[labelKey]}</option>
      {/each}
    </select>
  {:else if type === "file"}
    <input
      id={name} type="file" {name} bind:this={inputRef} class={cn("input w-full", inputClass, inputValidityClass)} {...inputProps}
      onblur={handleBlur}
      onchange={handleFileChange}
      {multiple}
    />
  {:else}
    <input
      id={name} {type} {name} bind:this={inputRef} bind:value={value} class={cn("input w-full", inputClass, inputValidityClass)} {...inputProps}
      onblur={handleBlur}
      oninput={handleInput}
    />
  {/if}
{/snippet}

{#if type === "hidden"}
  <input type="hidden" {name} {value} {...inputProps} />
{:else}

  <div class={containerClass}>
    <!-- If label is true (boolean) we want it to take up the space but not be displayed (alignment with another column) -->
    {#if labelContent}
      {@render labelContent({ name, baseClass: labelBaseClass })}
    {:else if label && type !== "checkbox"}
      <label class={cn(labelBaseClass, labelClass)} for={name}>{label === true ? "" : label}</label>
    {/if}

    {#if type === "email" || type === "password" || type === "textarea" || type === "color" || type === "richtext" || type === "checkbox" || type === "boolean" || type === "autocomplete" || type === "segmented-control"}
      {@render contents()}
    {:else if children}
      {@render children()}
    {:else}
      <InputContainer {prefix} {suffix} {prefixClass} {suffixClass}>
        {@render contents()}
      </InputContainer>
    {/if}

    {#if errorMessage}
      <p class="pl-1 text-[11px] text-red-600/80 error-text">{errorMessage}</p>
    {/if}

    {#if hint && valid}
      <p class="pl-1 text-[11px] text-gray-400 italic">{hint}</p>
    {/if}

    {@render innerContent?.()}

  </div>

{/if}
